import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import { CenteredLayout } from '../../../../components/CenteredLayout';
import { PreviewJobPost } from '../../../../entities/applicant_tracking/PreviewJobPost';
import { CompanyInfo } from '../../../../entities/CompanyInfo';
import JobPostPageHeader from './JobPostPageHeader';
import { ApplicationFormTemplate } from '../../../../entities/applicant_tracking/ApplicationFormTemplate';
import { ApplicationFormTab } from './ApplicationFormTab';
import { StateMachineProvider } from 'little-state-machine';
import { OverviewTab } from './OverviewTab';
import { CareersService } from '../../../../services/applicant_tracking/CareersService';
import { Source } from '../../../../entities/applicant_tracking/Source';

export type JobPostPageTab = 'overview' | 'application_form';

interface PropTypes {
  previewMode: boolean;
  previewJobPost: PreviewJobPost;
  applicationFormTemplate?: ApplicationFormTemplate;
  sources: Source[];
  jobPostHtmlString?: string;
  companyInfo?: CompanyInfo;
  activeTab?: JobPostPageTab;
}

function maxWidth(activeTab: string): string | undefined {
  if (activeTab === 'overview') return '800px';
  if (activeTab === 'application_form') return '600px';
}

function queryStringTab(): JobPostPageTab | null {
  const params = new URLSearchParams(window.location.search);
  const tab = params.get('tab');
  return tab as JobPostPageTab;
}

function defaultTab(viewSelectedTab?: JobPostPageTab) {
  return queryStringTab() ?? viewSelectedTab ?? 'overview';
}

export default function JobPostPage(props: PropTypes) {
  const [activeTab, setActiveTab] = useState<JobPostPageTab>(
    defaultTab(props.activeTab),
  );

  useEffect(() => {
    window.addEventListener('popstate', () => {
      const tab = queryStringTab();
      setActiveTab(tab as JobPostPageTab);
    });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    const urlTab = queryStringTab();
    if (urlTab != activeTab) {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', activeTab);

      history.pushState(null, '', url.toString());
    }
  }, [activeTab]);

  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    resetField,
    watch,
    formState,
  } = useForm<any>({
    mode: 'onChange',
  });

  const onSubmit = handleSubmit(async (data: any) => {
    const returnUrl = location.pathname;

    try {
      await CareersService.submitApplication(
        props.companyInfo?.subdomain,
        props.previewJobPost.jobPostId,
        {
          name: data.name,
          email: data.email,
          phoneNumber: data.phoneNumber,
          linkedinUrl: data.linkedin,
          resumeContent: data.resumeCurriculumVitaeContent,
          resumeFilename: data.resumeCurriculumVitaeFilename,
          coverLetterContent: data.coverLetterContent,
          coverLetterFilename: data.coverLetterFilename,
          portfolioUrl: data.portfolioWebsite,
          salaryExpectation: data.salaryExpectation,
          salaryCurrency: data.salaryCurrency,
          candidateConsented: data.candidateConsented,
          sourceId: data.sourceId,
          hasWorkingVisa: data.hasWorkingVisa,
          address: data.location?.address,
          city: data.location?.city,
          state: data.location?.state,
          country: data.location?.country,
          customQuestionAnswers: data.customQuestionAnswers,
        },
      );

      window.location.href = `${returnUrl}/submit/success`;
    } catch (e) {
      console.error(e);
      window.location.href = `${returnUrl}/submit/error`;
    }
  });

  return (
    <StateMachineProvider>
      <CenteredLayout heightClass='h-100'>
        <JobPostPageHeader
          previewMode={props.previewMode}
          previewJobPost={props.previewJobPost}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          companyInfo={props.companyInfo}
        />
        <form
          onSubmit={onSubmit}
          className={classNames('text-start', 'mx-auto')}
          style={{ maxWidth: maxWidth(activeTab) }}
        >
          {activeTab == 'overview' && (
            <OverviewTab
              previewMode={props.previewMode}
              jobPostHtmlString={props.jobPostHtmlString}
              setActiveTab={setActiveTab}
            />
          )}
          {activeTab === 'application_form' && (
            <ApplicationFormTab
              previewJobPost={props.previewJobPost}
              sources={props.sources}
              applicationFormTemplate={props.applicationFormTemplate}
              applicationFormTemplateConsent={
                props.previewJobPost.applicationFormTemplateCandidateConsent
              }
              previewMode={props.previewMode}
              formRegister={register}
              getValues={getValues}
              setValue={setValue}
              formControl={control}
              formResetField={resetField}
              formWatch={watch}
              formStateValid={formState.isValid}
            />
          )}
        </form>
      </CenteredLayout>
    </StateMachineProvider>
  );
}
